// Supabase client for the `when` consumer app. Lean mirror of apps/portal's
// lib/supabase.js — auth options pinned explicitly (token refresh + cross-tab
// session behaviour must survive supabase-js upgrades; the api client's implicit
// JWT read depends on persistSession + autoRefreshToken).
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('⚠️ Supabase not configured — set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
}

export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    })
  : null;

// The provider the user actually chose is carried through the OAuth round-trip
// as a `provider=` query param on redirectTo (alongside the existing `reconnect`
// and `next` params). It is NOT inferred from `session.user.app_metadata.provider`
// afterwards: that field is the provider the Supabase user was FIRST created with
// and never changes when a later identity (e.g. Microsoft) is linked to the same
// email. Routing on it sent email-first users down the email branch after a
// Microsoft sign-in, silently discarding the calendar tokens (2026-10-18).
export function withProvider(redirectTo, provider) {
  const url = new URL(redirectTo || `${window.location.origin}/auth/callback`);
  url.searchParams.set('provider', provider);
  return url.toString();
}

// Remember the provider of the current session (set by AuthCallback once the
// redirect param is read) so later flows that re-enter the same provider — e.g.
// Respond's "switch account" — don't have to fall back to app_metadata. Lives in
// localStorage next to the Supabase session; cleared on sign-out. Reads/writes are
// best-effort: storage may be blocked in private windows.
const PROVIDER_KEY = 'when_auth_provider';
export function rememberProvider(provider) {
  try { localStorage.setItem(PROVIDER_KEY, provider); } catch { /* best-effort */ }
}
export function recallProvider() {
  try { return localStorage.getItem(PROVIDER_KEY) || null; } catch { return null; }
}
export function forgetProvider() {
  try { localStorage.removeItem(PROVIDER_KEY); } catch { /* best-effort */ }
}

export const auth = {
  // Unified Google auth: sign-in IS the calendar connection. One grant covers
  // identity AND calendar, from the same account — so they can't diverge (this is
  // what makes the wrong-account attach impossible). `access_type=offline` yields a
  // refresh token on the first consent. `prompt=consent` is used ONLY to repair a
  // missing connection (`forceConsent`) — never every login, so returning users
  // don't re-consent each time. `redirectTo` must be absolute.
  async signInWithGoogle({ redirectTo, forceConsent = false, selectAccount = false } = {}) {
    // prompt precedence: consent (repair) wins over select_account (chooser).
    const prompt = forceConsent ? 'consent' : selectAccount ? 'select_account' : null;
    return supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: withProvider(redirectTo, 'google'),
        scopes: 'https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar.calendarlist.readonly',
        queryParams: {
          access_type: 'offline',
          ...(prompt ? { prompt } : {}),
        },
      },
    });
  },

  // Unified Microsoft (Azure) auth — the Microsoft equivalent of signInWithGoogle.
  // One Azure consent covers identity AND calendar (Graph Calendars.ReadWrite), so
  // they can't diverge. `offline_access` (a scope, not a query param as with Google)
  // yields the refresh token the API needs to refresh the login-issued token — which
  // requires Supabase's Azure provider and the API to share ONE app registration.
  async signInWithMicrosoft({ redirectTo, forceConsent = false, selectAccount = false } = {}) {
    const prompt = forceConsent ? 'consent' : selectAccount ? 'select_account' : null;
    return supabase.auth.signInWithOAuth({
      provider: 'azure',
      options: {
        redirectTo: withProvider(redirectTo, 'microsoft'),
        scopes: 'openid email profile offline_access https://graph.microsoft.com/Calendars.ReadWrite',
        ...(prompt ? { queryParams: { prompt } } : {}),
      },
    });
  },

  // Dispatch to the right provider sign-in. `provider` is 'google' | 'microsoft'.
  async signInWith(provider, opts = {}) {
    return provider === 'microsoft'
      ? this.signInWithMicrosoft(opts)
      : this.signInWithGoogle(opts);
  },

  // Email magic-link identity — for people who don't use Google/Microsoft (e.g.
  // iCloud-only users). This grants IDENTITY only, no calendar: the user connects
  // their iCloud calendar separately (CalDAV app-specific password) after landing.
  // Supabase sends the link via its Auth SMTP (not the app's Resend queue).
  async signInWithEmail(email, { redirectTo } = {}) {
    return supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: withProvider(redirectTo, 'email') },
    });
  },

  // Local sign-out only — never 'global' (a global sign-out revokes every device
  // session and can 401 a live tab mid-action).
  async signOut() {
    forgetProvider();
    return supabase.auth.signOut({ scope: 'local' });
  },

  async getSession() {
    const { data } = await supabase.auth.getSession();
    return data.session;
  },

  onAuthStateChange(cb) {
    return supabase.auth.onAuthStateChange(cb);
  },
};
